/**
 * Integration branding lookup — single source of truth for the
 * presentational layer of every integration listed in the backend
 * manifest (`firebase/functions/src/connectors/platform/integrations.yaml`).
 *
 * Why frontend-only?
 *   The backend manifest owns *what an integration is* (auth type,
 *   scopes, capabilities, allowed tiers). Branding is *how it looks*.
 *   Keeping logos and brand colors out of the GraphQL schema avoids
 *   touching the M2 trust boundary (ADR-0002) and lets us refresh a
 *   logo without a Firebase deploy.
 *
 * The two surfaces are joined by the integration's `id` — the same
 * stable string the manifest uses (e.g. "hubspot", "google-drive",
 * "microsoft-teams"). A coverage test in `IntegrationLogo.test.tsx`
 * asserts that every catalog id has either a Simple Icons slug or a
 * brand-color override, so a future integration added to the manifest
 * can't ship without at least *some* branding.
 *
 * Simple Icons honors brand removal requests, so several major
 * integrations (Slack, Salesforce, LinkedIn, DocuSign, the Microsoft
 * suite, Monday) are NOT in the package and fall back to a colored
 * monogram. That's intentional — the brand-color override table below
 * carries their official hex codes so the monogram stays on-brand.
 */

import * as si from 'simple-icons';

export interface IntegrationBranding {
  /** Hex without the leading `#`, e.g. `"FF7A59"`. */
  hex: string;
  /** Raw SVG path data — undefined when only the brand color is known. */
  path?: string;
  /** Display title, falls back to the catalog `name` when undefined. */
  title?: string;
}

/**
 * Map from manifest `integrationId` → Simple Icons export key
 * (`si<Pascal>`). Listed even for entries Simple Icons no longer
 * ships, so the lookup is exhaustive — `resolveBrandingFromSimpleIcons`
 * just returns `null` for missing modules.
 */
const SIMPLE_ICONS_SLUG: Record<string, string> = {
  airtable: 'siAirtable',
  calendly: 'siCalendly',
  docusign: 'siDocusign',
  dropbox: 'siDropbox',
  'google-calendar': 'siGooglecalendar',
  'google-drive': 'siGoogledrive',
  'google-mail': 'siGmail',
  hubspot: 'siHubspot',
  linkedin: 'siLinkedin',
  monday: 'siMondaydotcom',
  'microsoft-teams': 'siMicrosoftteams',
  notion: 'siNotion',
  outlook: 'siMicrosoftoutlook',
  quickbooks: 'siQuickbooks',
  salesforce: 'siSalesforce',
  sharepoint: 'siMicrosoftsharepoint',
  slack: 'siSlack',
  stripe: 'siStripe',
};

/**
 * Brand-color override table — official hex codes published by each
 * vendor's brand guidelines. Used both as the *fallback* color when
 * Simple Icons doesn't ship the brand, AND as the canonical color
 * for chips / borders / hover states across the UI even when Simple
 * Icons is present (so we have one source for accent color even if
 * Simple Icons revs a hex). When this table contradicts Simple Icons,
 * this table wins.
 */
const BRAND_COLOR_OVERRIDE: Record<string, string> = {
  // Simple Icons coverage gaps — vendors who policed their marks off:
  slack: '4A154B',
  monday: 'FF3D57',
  salesforce: '00A1E0',
  docusign: 'FFB81C',
  'microsoft-teams': '6264A7',
  outlook: '0078D4',
  sharepoint: '036C70',
  linkedin: '0A66C2',
};

interface SimpleIconExport {
  title: string;
  hex: string;
  path: string;
}

/** Look up a Simple Icons module by slug; returns null if absent. */
function lookupSimpleIcon(integrationId: string): SimpleIconExport | null {
  const key = SIMPLE_ICONS_SLUG[integrationId];
  if (!key) return null;
  // Simple Icons exports each brand as `si<Pascal>`; the package's
  // sideEffects: false flag plus webpack tree-shaking keeps the
  // bundle slim even when we touch the namespace import.
  const exported = (si as unknown as Record<string, SimpleIconExport | undefined>)[key];
  return exported ?? null;
}

/**
 * Resolve the visual branding for an integration. Returns `null` only
 * if the id is not in either table — callers should treat that as
 * "render the default MUI Avatar with the integration's first letter".
 */
export function resolveIntegrationBranding(
  integrationId: string,
): IntegrationBranding | null {
  const icon = lookupSimpleIcon(integrationId);
  const overrideHex = BRAND_COLOR_OVERRIDE[integrationId];

  if (icon) {
    return {
      hex: overrideHex ?? icon.hex,
      path: icon.path,
      title: icon.title,
    };
  }
  if (overrideHex) {
    return {hex: overrideHex};
  }
  return null;
}

/**
 * CSS color string (with the `#`) for the integration's brand color,
 * or undefined if no branding entry exists. Useful for borders,
 * chip backgrounds, and hover states.
 */
export function getBrandColor(integrationId: string): string | undefined {
  const branding = resolveIntegrationBranding(integrationId);
  return branding ? `#${branding.hex}` : undefined;
}

/**
 * Test-only helper: every integrationId currently known to the
 * frontend's branding layer. The coverage test diffs this against the
 * GraphQL catalog response to flag any future manifest addition that
 * forgot to register branding.
 */
export const KNOWN_INTEGRATION_IDS: ReadonlySet<string> = new Set([
  ...Object.keys(SIMPLE_ICONS_SLUG),
  ...Object.keys(BRAND_COLOR_OVERRIDE),
]);
